import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { PreviewDataWrapperComponent } from '../../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { Table, TableModule } from 'primeng/table';
import { PreviewDataType } from '../../../../shared/enums/preview-data-type.enum';
import { Dokument } from '../../../../shared/models/dokument';
import { DokumentApi } from '../../../../shared/services/api/dokument.api';
import { catchError, EMPTY, filter, finalize, tap } from 'rxjs';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ApplicationVisualizationDialog } from '../application-visualization-dialog/application-visualization-dialog';
import { IsTypDokumentuZusPipe } from '../../../../shared/pipes/is-typ-dokumentu-zus.pipe';
import { HttpUtils } from '../../../../shared/utils/http.utils';
import { SpinnerService } from '../../../../core/services/spinner.service';
import { VisualizationType } from '../../../../shared/enums/visualization-type.enum';
import { ExportType } from '../../../../shared/enums/export-type.enum';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmDialogComponent } from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { InboxApi } from '../../../../shared/services/api/inbox.api';
import { Zalacznik } from '../../../../shared/models/zalacznik';
import { ExportApi } from '../../../../shared/services/api/export.api';
import { VisualizationApi } from '../../../../shared/services/api/visualization.api';
import { TooltipModule } from 'primeng/tooltip';
import { PaginatorModule } from 'primeng/paginator';
import { MoveToOrganizationDialogComponent } from './move-to-organization-dialog-component';
import { AuthService } from '../../../../core/services/auth.service';
import { ApplicationContextService } from '../../../../core/services/application-context.service';
import { IsMoveToOrganizationActivePipe } from './is-move-to-organization-active.pipe';
import { PoswiadczenieStatusComponent } from '../poswiadczenie-status/poswiadczenie-status.component';
import { InboxType } from '../../../../shared/enums/inbox-type.enum';
import { DatePipe } from '@angular/common';
import { Poswiadczenie } from '../../../../shared/models/poswiadczenie';
import { HttpResponseService } from '../../../../core/services/http-error-response.service';
import { MessageService } from 'primeng/api';
import { ShouldShowCorrespondenceIconPipe } from '../../../../shared/pipes/should-show-correspondence-icon.pipe';
import { ApplicationService } from '../../../../shared/services/application.service';
import { ZS_6_APPLICATION } from '../../../application-cards/application-filters-definition';

@Component({
  standalone: true,
  selector: 'app-application-details',
  templateUrl: './application-details.component.html',
  styleUrl: './application-details.component.scss',
  imports: [
    Button,
    Card,
    PreviewDataWrapperComponent,
    TableModule,
    TooltipModule,
    IsTypDokumentuZusPipe,
    PaginatorModule,
    IsMoveToOrganizationActivePipe,
    DatePipe,
    ShouldShowCorrespondenceIconPipe,
  ],
  providers: [ExportApi, VisualizationApi, DokumentApi, InboxApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationDetailsComponent {
  readonly application = input.required<Dokument>();
  readonly applicationId = input.required<number>();
  @ViewChild('table') tableRef!: Table;
  readonly applicationContextService = inject(ApplicationContextService);
  readonly organizationsFromBeneficjent = computed(
    () => this.#beneficjent()?.organizacje ?? [],
  );
  protected readonly PreviewDataType = PreviewDataType;
  readonly #messageService = inject(MessageService);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  readonly #correspondenceDocumentProcessing = signal(false);
  protected readonly relatedDocuments = computed(
    () => this.application()?.dokumentyElektroniczne ?? [],
  );
  protected readonly selectedMessages = signal<Dokument[]>([]);
  protected isOrganizationContext = computed(() =>
    this.applicationContextService.isOrganizationContext(),
  );
  readonly #auth = inject(AuthService);
  #beneficjent = computed(() => this.#auth.getUser());
  readonly #visualizationApi = inject(VisualizationApi);
  readonly #applicationService = inject(ApplicationService);
  readonly #dialogRef = inject(DynamicDialogRef);
  readonly #httpResponseService = inject(HttpResponseService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #dialogService = inject(DialogService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #exportApi = inject(ExportApi);
  readonly #inboxApi = inject(InboxApi);
  protected inboxType = input<InboxType>();

  ngOnInit(): void {
    if (!this.applicationId()) {
      this.error.set('Brak identyfikatora wniosku.');
      return;
    }

    this.loading.set(false);
  }

  readonly isArchived = computed(() => {
    return this.inboxType() === InboxType.ARCHIVED;
  });

  protected downloadAttachment(attachment: Zalacznik) {
    this.#spinnerService.show();
    this.#exportApi
      .exportApplication$(attachment.id!, ExportType.ATTACHMENT)
      .pipe(
        tap((resp) => HttpUtils.downloadFile(resp)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Błąd podczas pobierania załącznika',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected exportRelatedApplication(id: number): void {
    this.#spinnerService.show();

    this.#exportApi
      .exportApplication$(id, ExportType.APPLICATION)
      .pipe(
        tap((resp) => HttpUtils.downloadFile(resp)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się wyeksportować powiązanego dokumentu',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected showPoswiadczenieStatus(
    poswiadczenie: Poswiadczenie | null,
    documentId: number,
  ): void {
    this.#dialogService.open(PoswiadczenieStatusComponent, {
      header: 'Status poświadczenia odbioru',
      width: '50vw',
      modal: true,
      closable: false,
      data: { poswiadczenie, documentId },
    });
  }

  protected restoreFromArchiveDocumentDialog(): void {
    const header = 'Chcesz przywrócić wybrany dokument z archiwum?';
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: {
        confirmButtonLabel: 'Ok',
      },
    });

    if (ref) {
      ref.onClose
        .pipe(
          filter(Boolean),
          tap(() => this.#restoreFromArchiveDocument()),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  #restoreFromArchiveDocument(): void {
    this.#spinnerService.show();

    this.#inboxApi
      .restoreFromArchiveDocuments$(new Set([this.application().id]))
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie przywrócono dokument z archiwum',
          );
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Wystąpił błąd podczas próby przywrócenia dokumentu z archiwum',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {
          this.#spinnerService.hide();
          this.#dialogRef.close('switch-to-sent');
        }),
      )
      .subscribe();
  }

  protected archiveDocumentDialog(): void {
    const ref = this.#dialogService.open(ConfirmDialogComponent, {
      closable: true,
      header: `Czy na pewno chcesz przenieść dokument do archiwum?`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: { confirmButtonLabel: 'Tak' },
    });

    ref?.onClose
      .pipe(
        filter(Boolean),
        tap(() => {
          this.#archiveDocument();
          this.#spinnerService.hide();
          this.#dialogRef.close('switch-to-archive');
        }),
        takeUntilDestroyed(this.#destroyRef),
        finalize(() => {}),
      )
      .subscribe();
  }

  protected showApplicationVisualization(): void {
    this.#visualizationApi
      .getVisualization$(this.application().id, VisualizationType.APPLICATION)
      .pipe(
        tap((content) => this.#openVisualizationDialog(content)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Błąd podczas wczytywania wizualizacji wniosku',
          );
          return EMPTY;
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected exportApplication(): void {
    this.#spinnerService.show();
    this.#exportApi
      .exportApplication$(this.application().id, ExportType.APPLICATION)
      .pipe(
        tap((resp) => HttpUtils.downloadFile(resp)),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się wyeksportować wniosku.',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  protected correspondRegardingApplication(dokument: Dokument): void {
    if (this.#correspondenceDocumentProcessing()) {
      return;
    }

    this.#correspondenceDocumentProcessing.set(true);
    this.#applicationService
      .initApplicationFlowWithIdKorelacji(ZS_6_APPLICATION, dokument.id)
      .pipe(
        finalize(() => {
          this.#correspondenceDocumentProcessing.set(false);
        }),
      )
      .subscribe();
  }

  protected openMoveToOrganizationDialog(): void {
    const organizations = this.organizationsFromBeneficjent();

    const ref = this.#dialogService.open(MoveToOrganizationDialogComponent, {
      header: 'Przenieś do organizacji',
      width: '30rem',
      modal: true,
      dismissableMask: true,
      data: { organizations },
    });

    if (ref != null) {
      ref.onClose
        .pipe(
          tap((selectedOrgId: number | null) => {
            if (!selectedOrgId) return;

            this.#submitMove(selectedOrgId);
          }),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe();
    }
  }

  #submitMove(orgId: number): void {
    const doc = this.application();
    if (!doc?.id) return;

    this.#spinnerService.show();

    this.#inboxApi
      .assignDocumentToOrganization$(doc.id, orgId)
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Wniosek został przeniesiony pomyślnie',
          );
          this.#dialogRef.close('reload');
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Nie udało się przenieść wniosku',
          );
          return EMPTY;
        }),
        finalize(() => this.#spinnerService.hide()),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #archiveDocument(): void {
    this.#spinnerService.show();
    this.#inboxApi
      .archiveDocuments$(new Set([this.application().id]))
      .pipe(
        tap(() => {
          this.#httpResponseService.showSuccessMessage(
            'Poprawnie przeniesiono dokument do archiwum',
          );
        }),
        catchError((err) => {
          this.#httpResponseService.showErrorMessage(
            err,
            'Błąd podczas archiwizacji dokumentu',
          );
          return EMPTY;
        }),
        finalize(() => {
          this.selectedMessages.set([]);
          this.#spinnerService.hide();
          this.#dialogRef.close('reload');
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();
  }

  #openVisualizationDialog(content: string): void {
    this.#dialogService.open(ApplicationVisualizationDialog, {
      closable: true,
      header: `Szczegóły wniosku`,
      closeOnEscape: true,
      modal: true,
      dismissableMask: true,
      inputValues: { content },
      style: {
        width: '100vw',
        height: '100vh',
        maxWidth: '100vw',
        maxHeight: '100vh',
        margin: '0',
      },
    });
  }

  protected readonly InboxType = InboxType;
}
